import React, { useContext, useState } from 'react';
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  DownOutlined,
  FilterOutlined,
  PlusOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { deleteData, fetchData } from '../../service/dataServices';
import CustomContent from '../../../components/Content/CustomContent';
import FilterDrawer from './FilterDrawer';
import { getTableMenuItems, pageSizeOptions } from '../../components/common-constants';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import { useExcelExporter } from '../../components/export/useExcelExporter';
import { FilterContext } from '../../../App';

const { Search } = Input;
const { Text } = Typography;

const ShowObjects = () => {
  const { t } = useTranslation();
  const hasWritePerm = hasWritePermission('item');
  const history = useHistory();
  const { objectFilters: filters, setObjectFilters: setFilters } = useContext(FilterContext);

  const [selectedItem, setSelectedItem] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState();
  const { excelExportModal, onExport } = useExcelExporter('objects', selectedRowKeys);

  const response = fetchData('/api/objects', data => setData(data.content), filters);
  const deleteMutation = deleteData('/api/objects');

  const onMenuClick = action => {
    if (action.key === 'edit' && selectedItem.id) {
      history.push('/save-object/' + selectedItem.id);
    }
  };

  const isFiltered = (filters) => {
    if (filters) {
      const result = Object.keys(filters).filter(k => k !== "page" && k !== "size" && !!filters[k]);
      return result && result.length > 0;
    }
    return false;
  }

  const handleSingleDelete = () => {
    deleteMutation.mutate({ idList: selectedItem.id });
  };

  const handleTableChange = pagination => {
    if (pagination.current - 1 !== filters?.page) {
      setFilters(prevState => ({
        ...prevState,
        page: pagination.current - 1,
      }));
    }
    if (pagination.pageSize !== filters?.size) {
      setFilters(prevState => ({
        ...prevState,
        size: pagination.pageSize,
      }));
    }
  };

  const onSearch = value => {
    if (value !== '') {
      setFilters(prevState => ({
        ...prevState,
        searchAll: value,
        page: 0,
      }));
    } else {
      const val = { ...filters };
      delete val.searchAll;
      val.page = 0;
      setFilters(val);
    }
  };

  const onDelete = () => {
    deleteMutation.mutate({ idList: selectedRowKeys.join(',') });
    setSelectedRowKeys([]);
  };

  const onAdd = () => {
    history.push('/add-object');
  };

  const menu = getTableMenuItems(t, onMenuClick, handleSingleDelete);

  const columns = [
    {
      title: t('objects.inventoryNumber'),
      dataIndex: 'inventoryNumber',
      key: 'inventoryNumber',
      render: (text, record) => (
        <>
          <Button
            type="link"
            onClick={() => history.push('/save-object/' + record.id)}
          >
            {text}
          </Button>
          <Text copyable={{ text: text }}></Text>
        </>
      ),
    },
    {
      title: t('objects.objectName'),
      dataIndex: 'objectName',
      key: 'objectName',
    },
    {
      title: t('objects.location'),
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: t('objects.locationArea'),
      dataIndex: 'locationArea',
      key: 'locationArea',
    },
    {
      title: t('objects.actions'),
      key: 'action',
      render: () => hasWritePerm ? (
        <span>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button type="link">
              {t('objects.actions')} <DownOutlined />
            </Button>
          </Dropdown>
        </span>
      ) : '',
    },
  ];

  const downloadTxtFile = (text) => {
    const element = document.createElement("a");
    const file = new Blob([text], {
      type: "text/plain"
    });
    element.href = URL.createObjectURL(file);
    element.download = "gaps.txt";
    document.body.appendChild(element);
    element.click();
  };

  const gaps = fetchData('/api/objects/gap', (data) => {
    downloadTxtFile(data.join('\n'))
  }, undefined, false);

  const onExportGaps = () => {
    gaps.refetch().then(resp => {
      // console.log("2", data);
    });
  }
  return (
    <CustomContent>
      <Row>
        <Col span={18}>
          <Space direction="horizontal" style={{ width: '100%' }} wrap>
            <Search
              defaultValue={filters ? filters.searchAll : undefined}
              style={{ width: '30vh' }}
              placeholder={t('objects.search')}
              onSearch={value => onSearch(value)}
              allowClear
            />
            <Button
              icon={<FilterOutlined />}
              onClick={() => setDrawerVisible(true)}
              danger={isFiltered(filters)}
            >
              {t('objects.filters')}
            </Button>
          </Space>
        </Col>
        <Col span={6}>
          <Row justify="end">
            <Space direction="horizontal">
              {/* <Button
                onClick={onExportGaps}
                icon={<DownloadOutlined />}
              >
                {t('objects.gaps')}
              </Button> */}
              <Button
                onClick={onExport}
                icon={<DownloadOutlined />}
              >
                {t('objects.export')}
              </Button>
              {hasWritePerm && (
                <>
                  <Popconfirm
                    title={t('objects.confirmation')}
                    description={t('objects.deleteConfirm')}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    onConfirm={onDelete}
                    okButtonProps={{ size: 'middle' }}
                    okText={t('objects.yes')}
                    cancelButtonProps={{ size: 'middle' }}
                    styles={{ body: { padding: 16 } }}
                    disabled={selectedRowKeys.length === 0}
                  >
                    <Button
                      disabled={selectedRowKeys.length === 0}
                      icon={<DeleteOutlined />}
                    >
                      {t('objects.delete')}
                    </Button>
                  </Popconfirm>
                  <Button onClick={onAdd} icon={<PlusOutlined />} type="primary">
                    {t('objects.add')}
                  </Button>
                </>
              )}
            </Space>
          </Row>
        </Col>
      </Row>
      <Divider />
      <Table
        dataSource={data}
        columns={columns}
        loading={response.isFetching}
        rowKey={record => record.id}
        onChange={handleTableChange}
        size='middle'
        pagination={{
          pageSize: filters?.size,
          total: response.data ? response.data.data.totalElements : 0,
          showSizeChanger: true,
          current: filters ? filters.page + 1 : 1,
          pageSizeOptions: pageSizeOptions,
          showTotal: (total, range) => {
            return t('common.paginationTotal', { start: range[0], end: range[1], total });
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: selected => setSelectedRowKeys(selected),
        }}
        onRow={record => {
          return {
            onClick: () => setSelectedItem(record),
          };
        }}
      />
      <FilterDrawer
        filters={filters}
        drawerVisible={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        setFilters={setFilters}
      />
      {excelExportModal}
    </CustomContent>
  );
};

export default ShowObjects;
